import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CampaignImage, PartyAward, SceneMood, UpdateStatus } from "@dm/shared";
import type {
  Campaign,
  CampaignEvent,
  CampaignRole,
  Character,
  CombatState,
  DiceRoll,
  GameSession,
  LiveState,
  RollRequest,
  User,
} from "@dm/shared";
import { can } from "@dm/shared";
import { api } from "./api.js";

/** Shared live game state for the web table, kept in sync with Server-Sent Events. */
export interface Game {
  campaign: Campaign;
  user: User;
  role: CampaignRole | null;
  isAdmin: boolean;
  /** Host, Co-host or Admin: runs the game (any character, combat, roll requests, invites). */
  isHost: boolean;
  /** The campaign's Host (or Admin): settings, roles, deletion. */
  isOwner: boolean;
  /** Live session: is the DM in voice and is tonight's host there? */
  session: LiveState | null;
  setSession: (l: LiveState) => void;
  /** Tonight's play session (started when the DM joins voice or with Start Session), or null. */
  playSession: GameSession | null;
  setPlaySession: (s: GameSession | null) => void;
  /** Bumped whenever a session starts, ends or its recap/summary changes. */
  sessionsVersion: number;
  /** Bumped when my private whisper thread changes. */
  whisperVersion: number;
  /** Bumped when the Host panel data changes (Hosts only care). */
  hostVersion: number;
  /** Transcript lines a Host rewound since this page loaded (struck out live). */
  rewoundLines: ReadonlySet<number>;
  /** Bumped when the open session's spending changes (Phase 4). */
  costVersion: number;
  /** A session that just ended while this page was open (for the cost popup). */
  endedSessionId: number | null;
  clearEndedSession: () => void;
  /** Latest server-update status pushed live (countdown banner). */
  update: UpdateStatus | null;
  /** Phase 5: awards seen live (popups), newest last. */
  awards: PartyAward[];
  dismissAward: (id: number) => void;
  /** Bumped when loot offers change. */
  lootVersion: number;
  adventureVersion: number;
  /** Bumped when the campaign's schedule changes. */
  scheduleVersion: number;
  /** Phase 6.3: the table's ambient music mood (live). */
  mood: SceneMood;
  /** "DM is thinking…": who the DM is answering, or null when it isn't working on a reply. */
  dmThinking: { for: string | null } | null;
  /** Bumped when a picture is shown; popupImage = the newest one not yet dismissed. */
  imageVersion: number;
  /** Phase 7: bumped when the battle map or its tokens change. */
  mapVersion: number;
  popupImage: CampaignImage | null;
  clearPopupImage: () => void;
  /** Bumped when one of my appeals was decided (or the Host panel changed). */
  appealVersion: number;
  party: Character[];
  myCharacter: Character | null;
  combat: CombatState | null;
  rolls: DiceRoll[];
  requests: RollRequest[];
  /** false while the live connection is down (it reconnects automatically). */
  live: boolean;
  refreshParty: () => Promise<void>;
  setCombat: (c: CombatState | null) => void;
  /** Report an error from an action (shown in the toast area). */
  fail: (err: unknown) => void;
  error: string | null;
  clearError: () => void;
}

const GameContext = createContext<Game | null>(null);

export function useGame(): Game {
  const g = useContext(GameContext);
  if (!g) throw new Error("useGame outside GameProvider");
  return g;
}

export function GameProvider(props: {
  campaign: Campaign;
  user: User;
  role: CampaignRole | null;
  isAdmin: boolean;
  /** Settings or membership changed (reload /api/me). */
  onCampaignChanged: () => void;
  children: ReactNode;
}) {
  const { campaign, user, role, isAdmin, onCampaignChanged } = props;
  const [session, setSession] = useState<LiveState | null>(null);
  const [playSession, setPlaySession] = useState<GameSession | null>(null);
  const [sessionsVersion, setSessionsVersion] = useState(0);
  const [whisperVersion, setWhisperVersion] = useState(0);
  const [hostVersion, setHostVersion] = useState(0);
  const [rewoundLines, setRewoundLines] = useState<ReadonlySet<number>>(new Set());
  const [costVersion, setCostVersion] = useState(0);
  const [endedSessionId, setEndedSessionId] = useState<number | null>(null);
  const clearEndedSession = useCallback(() => setEndedSessionId(null), []);
  const [update, setUpdate] = useState<UpdateStatus | null>(null);
  const [awards, setAwards] = useState<PartyAward[]>([]);
  const dismissAward = useCallback((id: number) => setAwards((a) => a.filter((x) => x.id !== id)), []);
  const [lootVersion, setLootVersion] = useState(0);
  const [adventureVersion, setAdventureVersion] = useState(0);
  const [mood, setMood] = useState<SceneMood>(campaign.sceneMood ?? "calm");
  const [dmThinking, setDmThinking] = useState<{ for: string | null } | null>(null);
  const thinkingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setMood(campaign.sceneMood ?? "calm"); }, [campaign.sceneMood]);
  const [imageVersion, setImageVersion] = useState(0);
  const [mapVersion, setMapVersion] = useState(0);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [popupImage, setPopupImage] = useState<CampaignImage | null>(null);
  const clearPopupImage = useCallback(() => setPopupImage(null), []);
  const [appealVersion, setAppealVersion] = useState(0);
  const [party, setParty] = useState<Character[]>([]);
  const [combat, setCombat] = useState<CombatState | null>(null);
  const [rolls, setRolls] = useState<DiceRoll[]>([]);
  const [requests, setRequests] = useState<RollRequest[]>([]);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fail = useCallback((err: unknown) => {
    setError(err instanceof Error ? err.message : String(err));
    clearTimeout(errTimer.current);
    errTimer.current = setTimeout(() => setError(null), 6000);
  }, []);

  const refreshParty = useCallback(async () => {
    setParty((await api.party(campaign.id)).characters);
  }, [campaign.id]);
  const refreshRequests = useCallback(async () => {
    setRequests((await api.rollRequests(campaign.id)).requests);
  }, [campaign.id]);
  const refreshAll = useCallback(async () => {
    const [p, c, r, q] = await Promise.all([
      api.party(campaign.id),
      api.combat(campaign.id),
      api.rolls(campaign.id),
      api.rollRequests(campaign.id),
    ]);
    setParty(p.characters);
    setCombat(c.combat);
    setRolls(r.rolls);
    setRequests(q.requests);
    setSession(await api.live(campaign.id));
    setPlaySession((await api.sessions(campaign.id)).current);
  }, [campaign.id]);

  useEffect(() => {
    refreshAll().catch(fail);
    const es = new EventSource(`/api/campaigns/${campaign.id}/events`);
    let wasDown = false;
    es.onopen = () => {
      setLive(true);
      // Resync anything we missed while disconnected.
      if (wasDown) refreshAll().catch(() => undefined);
      wasDown = false;
    };
    es.onerror = () => {
      setLive(false);
      wasDown = true;
    };
    es.onmessage = (msg) => {
      let ev: CampaignEvent;
      try {
        ev = JSON.parse(msg.data as string) as CampaignEvent;
      } catch {
        return;
      }
      switch (ev.type) {
        case "roll":
          setRolls((prev) => (prev.some((r) => r.id === ev.roll.id) ? prev : [ev.roll, ...prev].slice(0, 100)));
          break;
        case "party":
          refreshParty().catch(() => undefined);
          break;
        case "combat":
          setCombat(ev.combat);
          break;
        case "rollRequests":
          refreshRequests().catch(() => undefined);
          break;
        case "map":
          setMapVersion((v) => v + 1);
          break;
        case "live":
          setSession(ev.live);
          break;
        case "campaign":
          onCampaignChanged();
          break;
        case "session":
          if (ev.started || (!ev.session.endedAt && !ev.ended)) setPlaySession(ev.session);
          else if (ev.ended) setPlaySession((cur) => (cur?.id === ev.session.id ? null : cur));
          setSessionsVersion((v) => v + 1);
          break;
        case "whisper":
          setWhisperVersion((v) => v + 1);
          break;
        case "hostPanel":
          setHostVersion((v) => v + 1);
          break;
        case "rewind":
          setRewoundLines((prev) => new Set([...prev, ...ev.lineIds]));
          setHostVersion((v) => v + 1);
          break;
        case "cost":
          setCostVersion((v) => v + 1);
          break;
        case "update":
          setUpdate(ev.status);
          break;
        case "award":
          setAwards((a) => [...a.filter((x) => x.id !== ev.award.id), ev.award].slice(-4));
          break;
        case "loot":
          setLootVersion((v) => v + 1);
          break;
        case "adventure":
          setAdventureVersion((v) => v + 1);
          break;
        case "mood":
          setMood(ev.mood);
          break;
        case "thinking":
          if (thinkingTimer.current) clearTimeout(thinkingTimer.current);
          setDmThinking(ev.on ? { for: ev.for ?? null } : null);
          // Never stuck on: clears itself after 60 s even if the "off" event is lost.
          if (ev.on) thinkingTimer.current = setTimeout(() => setDmThinking(null), 60_000);
          break;
        case "transcript":
          if (ev.line.speakerName === "Dungeon Master") setDmThinking(null);
          break;
        case "schedule":
          setScheduleVersion((v) => v + 1);
          break;
        case "imagesChanged":
          setImageVersion((v) => v + 1);
          break;
        case "image":
          setImageVersion((v) => v + 1);
          setPopupImage(ev.image);
          break;
        case "appeal":
          setAppealVersion((v) => v + 1);
          break;
      }
      if (ev.type === "session" && ev.ended) setEndedSessionId(ev.session.id);
    };
    return () => es.close();
  }, [campaign.id, refreshAll, refreshParty, refreshRequests, fail, onCampaignChanged]);

  const value = useMemo<Game>(
    () => ({
      campaign,
      user,
      role,
      isAdmin,
      isHost: can("runGame", { admin: isAdmin, role }),
      isOwner: can("editSettings", { admin: isAdmin, role }),
      session,
      setSession,
      playSession,
      setPlaySession,
      sessionsVersion,
      whisperVersion,
      hostVersion,
      rewoundLines,
      costVersion,
      endedSessionId,
      clearEndedSession,
      update,
      awards,
      dismissAward,
      lootVersion,
      adventureVersion,
      mood,
      dmThinking,
      scheduleVersion,
      imageVersion,
      mapVersion,
      popupImage,
      clearPopupImage,
      appealVersion,
      party,
      myCharacter: party.find((c) => c.ownerDiscordId === user.discordId) ?? null,
      combat,
      rolls,
      requests,
      live,
      refreshParty,
      setCombat,
      fail,
      error,
      clearError: () => setError(null),
    }),
    [campaign, user, role, isAdmin, session, playSession, sessionsVersion, whisperVersion, hostVersion, rewoundLines, costVersion, endedSessionId, clearEndedSession, update, awards, dismissAward, lootVersion, adventureVersion, mood, dmThinking, scheduleVersion, imageVersion, mapVersion, popupImage, clearPopupImage, appealVersion, party, combat, rolls, requests, live, refreshParty, fail, error],
  );
  return <GameContext.Provider value={value}>{props.children}</GameContext.Provider>;
}
